import { encodeReplayControls } from "./controlBits.js";
import { MAX_REPLAY_CHECKPOINTS, MAX_REPLAY_EVENTS, MAX_REPLAY_STATE_HASHES, REPLAY_SCHEMA_VERSION_NUMBER, REPLAY_TICK_RATE, stateHash, validateReplayEnvelope, } from "./schema.js";
import { quantizeVehicleState } from "../simulation/version.js";
export class InputRecorder {
    options;
    events = [];
    checkpoints = [];
    stateHashes = [];
    lastControls = null;
    lastTick;
    ended = false;
    constructor(options) {
        if (!Number.isSafeInteger(options.startTick) || options.startTick < 0) {
            throw new Error("Recorder start tick must be a non-negative integer");
        }
        const stateHashInterval = options.stateHashInterval ?? 60;
        const maxTicks = options.maxTicks ?? 60 * 60 * 30;
        if (!Number.isSafeInteger(stateHashInterval) || stateHashInterval < 1) {
            throw new Error("Recorder stateHashInterval must be a positive integer");
        }
        if (!Number.isSafeInteger(maxTicks) ||
            maxTicks < 1 ||
            maxTicks > 60 * 60 * 30) {
            throw new Error("Recorder maxTicks is outside the supported duration");
        }
        this.options = {
            ...options,
            stateHashInterval,
            maxTicks,
            initialState: quantizeVehicleState(options.initialState),
        };
        this.lastTick = options.startTick - 1;
    }
    recordTick(tick, controls, state) {
        this.ensureOpen();
        if (!Number.isSafeInteger(tick) || tick !== this.lastTick + 1) {
            throw new Error("Recorder ticks must be contiguous");
        }
        if (tick - this.options.startTick >= this.options.maxTicks) {
            throw new Error("Replay duration exceeds the recorder limit");
        }
        const encoded = encodeReplayControls(controls);
        if (this.lastControls === null || encoded !== this.lastControls) {
            if (this.events.length >= MAX_REPLAY_EVENTS)
                throw new Error("Replay event limit exceeded");
            this.events.push({ tick, controls: encoded });
            this.lastControls = encoded;
        }
        if (state &&
            (tick - this.options.startTick) % this.options.stateHashInterval === 0) {
            if (this.stateHashes.length >= MAX_REPLAY_STATE_HASHES)
                throw new Error("Replay state hash limit exceeded");
            this.stateHashes.push({ tick, hash: stateHash(state) });
        }
        this.lastTick = tick;
    }
    recordCheckpoint(index, tick) {
        this.ensureOpen();
        if (!Number.isSafeInteger(index) ||
            index < 0 ||
            index > 2 ||
            !Number.isSafeInteger(tick)) {
            throw new Error("Invalid replay checkpoint");
        }
        if (tick < this.options.startTick || tick > this.lastTick) {
            throw new Error("Replay checkpoint is outside recorded ticks");
        }
        const previous = this.checkpoints[this.checkpoints.length - 1];
        if (!previous && index !== 0) {
            throw new Error("Replay checkpoints must begin with red index 0");
        }
        if (previous && index !== (previous.index + 1) % 3) {
            throw new Error("Replay checkpoints must follow red, green, blue order");
        }
        if (previous && tick <= previous.tick)
            throw new Error("Replay checkpoints must be ordered");
        if (this.checkpoints.length >= MAX_REPLAY_CHECKPOINTS)
            throw new Error("Replay checkpoint limit exceeded");
        this.checkpoints.push({ index, tick });
    }
    finalize(endTick = this.lastTick) {
        this.ensureOpen();
        if (this.lastTick < this.options.startTick) {
            throw new Error("Cannot finalize a replay before its first tick");
        }
        if (endTick !== this.lastTick)
            throw new Error("Replay end tick must match the last recorded tick");
        const replay = this.snapshot();
        this.ended = true;
        return replay;
    }
    /** Return the current cumulative attempt without stopping recording. */
    snapshot(attemptId = this.options.attemptId) {
        this.ensureOpen();
        const replay = {
            schemaVersion: REPLAY_SCHEMA_VERSION_NUMBER,
            tickRate: REPLAY_TICK_RATE,
            trackId: this.options.trackId,
            carId: this.options.carId,
            trackMapHash: this.options.trackMapHash,
            carConfigHash: this.options.carConfigHash,
            ...(this.options.rulesetId ? { rulesetId: this.options.rulesetId } : {}),
            attemptId,
            startTick: this.options.startTick,
            endTick: this.lastTick,
            initialState: quantizeVehicleState(this.options.initialState),
            events: this.events.map((event) => ({ ...event })),
            checkpoints: this.checkpoints.map((checkpoint) => ({ ...checkpoint })),
            stateHashes: this.stateHashes.map((entry) => ({ ...entry })),
        };
        validateReplayEnvelope(replay);
        return replay;
    }
    ensureOpen() {
        if (this.ended)
            throw new Error("Replay recorder is already finalized");
    }
}
